// DRF reports validation errors as a tree: an object keyed by field name, a
// list aligned with the items that were sent for a nested collection (the ones
// that validated come back empty), and a list of messages at the leaves.
//
// Concatenating a key with its value - `key + ": " + value` - only works for
// the flat case. As soon as a nested serializer is involved the value is an
// array of objects and the toaster ends up showing "actions: [object Object]",
// so the tree has to be walked down to the message.

const NON_FIELD_KEYS = ["non_field_errors", "nonFieldErrors", "detail"];

const isPlainObject = (value) =>
  typeof value === "object" && value !== null && !Array.isArray(value);

const labelFor = (key, labels) => labels[key] || key;

// Returns `{ path, message }` for the first message found, or null.
const findFirstError = (node, path, labels) => {
  if (typeof node === "string" && node) {
    return { path: path, message: node };
  }

  if (Array.isArray(node)) {
    for (let index = 0; index < node.length; index += 1) {
      const entry = node[index];
      // An entry that is itself a tree comes from a nested collection, so its
      // position is worth naming - a message from the third cost of the second
      // action says nothing without it. A plain message carries no position.
      let entryPath = path;
      if (isPlainObject(entry) && path.length) {
        entryPath = path
          .slice(0, -1)
          .concat(path[path.length - 1] + " #" + (index + 1));
      }

      const found = findFirstError(entry, entryPath, labels);
      if (found) {
        return found;
      }
    }

    return null;
  }

  if (isPlainObject(node)) {
    const keys = Object.keys(node);
    for (let index = 0; index < keys.length; index += 1) {
      const key = keys[index];
      // Errors that belong to the whole object have no field to point at.
      const keyPath = NON_FIELD_KEYS.includes(key)
        ? path
        : path.concat(labelFor(key, labels));

      const found = findFirstError(node[key], keyPath, labels);
      if (found) {
        return found;
      }
    }

    return null;
  }

  return null;
};

/**
 * Builds a single readable message out of a DRF error payload.
 *
 * @param {*} data - `response.data` of a rejected request.
 * @param {string} [fallback] - used when the payload carries no message.
 * @param {Object} [labels] - maps backend field names to UI labels, so the
 *   path reads the way the form does.
 * @returns {string}
 */
export const firstServerError = (data, fallback, labels) => {
  const found = findFirstError(data, [], labels || {});

  if (!found) {
    return fallback || "";
  }

  if (!found.path.length) {
    return found.message;
  }

  return found.path.join(" → ") + ": " + found.message;
};

/**
 * Reads the first message of a single field out of one node of the error tree.
 * DRF puts a list of messages at the leaves, but some validators answer with a
 * bare string - both shapes have to render.
 *
 * @param {*} errors - the node holding the field, e.g. `data.actions[2]`.
 * @param {string} field
 * @returns {string}
 */
export const fieldError = (errors, field) => {
  const error = isPlainObject(errors) ? errors[field] : undefined;

  if (Array.isArray(error)) {
    return typeof error[0] === "string" ? error[0] : "";
  }

  return typeof error === "string" ? error : "";
};

/**
 * Picks the error node of one item of a nested collection, e.g. the third cost
 * of an action. Entries that validated come back empty, and a collection can
 * also fail as a whole, in which case there is no per-item node at all.
 *
 * @param {*} errors - the node holding the collection.
 * @param {string} field - the collection field name.
 * @param {number} index - position of the item within what was sent.
 * @returns {Object}
 */
export const collectionItemErrors = (errors, field, index) => {
  const items = isPlainObject(errors) ? errors[field] : undefined;

  if (!Array.isArray(items)) {
    return {};
  }

  return isPlainObject(items[index]) ? items[index] : {};
};
